import { Component, Input, Output, EventEmitter, SimpleChanges, OnInit, OnChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn } from '@angular/forms';
import { NetViewTreeNodeDto } from 'UvExplorerServer.Dtos.TS/NetViews';
import { SmtpSettings } from 'DiscoveryEngine.TS/Smtp';
import { ComponentBase, Changeable } from "common";
import { ConfigurationService } from 'configuration';
import { ResultDto } from 'UvExplorerServer.Dtos.TS';
import { MessageService } from 'base';

@Component({
    selector: 'email-settings',
	templateUrl: './email-settings.component.html',
    styleUrls: ['./email-settings.component.scss']
})

export class EmailSettingsComponent extends ComponentBase implements OnInit, OnChanges, Changeable {

	@Input() public target: NetViewTreeNodeDto;
	public get hasTarget(): boolean {
		return !!this.target;
	}

	public settingsForm: UntypedFormGroup;

	public serverAddress: UntypedFormControl;
	public serverPort: UntypedFormControl;
	public timeout: UntypedFormControl;
	public toAddress: UntypedFormControl;
	public fromAddress: UntypedFormControl;
	public fromName: UntypedFormControl;
	public requiresAuth: UntypedFormControl;
	public requiresEncryption: UntypedFormControl;
	public userName: UntypedFormControl;
	public password: UntypedFormControl;
	public confirmPassword: UntypedFormControl;

	constructor(private fb: UntypedFormBuilder,
				private configService: ConfigurationService,
				private messageService: MessageService) {
		super();

		this.buildForm();
    }

    ngOnInit() {
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.buildForm();
	}

	private buildForm(): void {

		let source: SmtpSettings;
		if (this.hasTarget && this.target.HasSmtpSettings) {
			source = this.target.SmtpSettings;
		}
		else {
			source = new SmtpSettings();
		}

		let serverAddress = source.ServerAddress;
		let serverPort = source.ServerPort;
		let timeout = source.Timeout / 1000;
		let toAddress = source.ToAddress;
		let fromAddress = source.FromAddress;
		let fromName = source.FromName;
		let requiresAuth = source.RequiresAuth;
		let requiresEncryption = source.RequiresEncryption;
		let userName = source.UserName;
		let password = source.Password;
		let confirmPassword = source.Password;

		//this.enableDisableControls(requiresAuth);

		this.serverAddress = new UntypedFormControl(serverAddress, Validators.required);
		this.serverPort = new UntypedFormControl(serverPort, Validators.compose([Validators.required, this.minValue(1), this.maxValue(65535)]));
		this.timeout = new UntypedFormControl(timeout, Validators.compose([Validators.required, this.minValue(1), this.maxValue(200)]));
		this.toAddress = new UntypedFormControl(toAddress, Validators.required);
		this.fromAddress = new UntypedFormControl(fromAddress, Validators.required);
		this.fromName = new UntypedFormControl(fromName, Validators.required);
		this.requiresAuth = new UntypedFormControl(requiresAuth);
		this.requiresEncryption = new UntypedFormControl(requiresEncryption);
		this.userName = new UntypedFormControl(userName);
		this.password = new UntypedFormControl(password);
		this.confirmPassword = new UntypedFormControl(confirmPassword);

		this.settingsForm = this.fb.group({
				serverAddress: this.serverAddress,
				serverPort: this.serverPort,
				timeout: this.timeout,
				toAddress: this.toAddress,
				fromAddress: this.fromAddress,
				fromName: this.fromName,
				requiresAuth: this.requiresAuth,
				requiresEncryption: this.requiresEncryption,
				userName: this.userName,
				password: this.password,
				confirmPassword: this.confirmPassword
			},
			{
				validator: Validators.compose([
					this.validUserName(this.requiresAuth, this.userName),
					this.validPassword(this.requiresAuth, this.password),
					this.passwordsEqual(this.requiresAuth, this.password, this.confirmPassword)
				])
			}
		);
		
	}

	private validUserName(requiresAuth: UntypedFormControl, userName: UntypedFormControl): ValidatorFn {
		return () => {
			if (requiresAuth.value) {
				if (!userName.value) {
					return {
						validUserName: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	private validPassword(requiresAuth: UntypedFormControl, password: UntypedFormControl): ValidatorFn {
		return () => {
			if (requiresAuth.value) {
				if (!password.value) {
					return {
						validPassword: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	private passwordsEqual(requiresAuth: UntypedFormControl, password: UntypedFormControl,
							confirmPassword: UntypedFormControl): ValidatorFn {
		return () => {
			if (requiresAuth.value) {
				if (password.value !== confirmPassword.value) {
					return {
						passwordsEqual: { valid: false }
					};
				}
				else {
					return null;
				}
			}
		}
	}

	public get enableTestEmail(): boolean {
		return (this.settingsForm && this.settingsForm.valid);
	}
	
	public testEmail(): void {
		if (this.enableTestEmail) {
			let smtp: SmtpSettings = this.getSmtpSettings();
			if (smtp) {
				this.configService.testEmail(smtp).subscribe(
					(result: ResultDto) => {
						if (result.Succeeded) {
							this.messageService.displayInfoMessage("Email test succeeded");
						}
						else {
							this.messageService.displayErrorMessage("Email test failed: " + result.Message);
						}
							},
					(error) => {
						this.messageService.displayErrorMessage("Email test failed");
						console.error(error);
					});
			}
		}
	}


	// Changeable

	public get tempChanges(): boolean {
		return this.settingsForm.dirty;
	}

	public get tempChangesValid(): boolean {
		return this.settingsForm.valid;
	}

	public get invalidTempChanges(): string[] {
		return ["Email Settings"];
	}

	public applyTempChanges(): void {
		let smtp: SmtpSettings = this.getSmtpSettings();
		this.target.SmtpSettings = smtp;
		this.target.HasChanges = true;
		this.buildForm();
	}

	private getSmtpSettings(): SmtpSettings {
		let smtp = new SmtpSettings();

		smtp.ServerAddress = this.serverAddress.value;
		smtp.ServerPort = parseInt(this.serverPort.value);
		smtp.Timeout = parseInt(this.timeout.value) * 1000;
		smtp.ToAddress = this.toAddress.value;
		smtp.FromAddress = this.fromAddress.value;
		smtp.FromName = this.fromName.value;
		smtp.RequiresAuth = this.requiresAuth.value;
		smtp.RequiresEncryption = this.requiresEncryption.value;
		smtp.UserName = this.userName.value;
		smtp.Password = this.password.value;

		return smtp;
	}

	public discardTempChanges(): void {
		this.buildForm();
	}
}
